/**
 * 出発地マスタ（都道府県 → 出発空港）
 *
 * なぜ都道府県で選ばせるか：
 *   IATA コード（HND / ITM …）を知っている一般利用者は少ない。
 *   「自分の住んでいる県」を選べば出発空港が決まる形にする。
 *
 * 収録方針（推測でコードを作らない）：
 *   - 定期旅客便のある空港のうち、その県からの現実的な出発地になるものだけを列挙。
 *     離島路線専用の小規模空港（利尻・奥尻・多数の長崎離島など）は出発地として非現実的なため除外。
 *   - 県内に定期便空港が無い県（埼玉・神奈川・群馬・栃木・山梨・岐阜・滋賀・京都・奈良・三重・福井）は
 *     `borrowed: true` を立て、最寄りの主要空港を案内する。**その県に空港があるとは表示しない。**
 *   - 成田(NRT)は千葉県所在。ただし首都圏の出発地として実用上は東京からも選ばれるため、
 *     東京都にも所在を明示（「成田空港（千葉）」）した上で載せる。
 */

export interface DepartureAirport {
  /** IATA 3レターコード */
  iata: string;
  /** 表示名（空港名。所在が紛らわしいものは括弧で補足） */
  nameJa: string;
}

export interface DeparturePrefecture {
  /** 内部コード（localStorage / URL 用・不変） */
  code: string;
  /** 都道府県名 */
  nameJa: string;
  /** 地方区分（select の optgroup） */
  region: string;
  /** 出発空港。先頭が既定 */
  airports: DepartureAirport[];
  /** true = 県内に定期便の空港が無く、近隣県の空港を案内している */
  borrowed?: boolean;
}

export const DEPARTURE_REGIONS = [
  '北海道',
  '東北',
  '関東',
  '中部',
  '近畿',
  '中国',
  '四国',
  '九州',
  '沖縄',
] as const;

// 県をまたいで使い回す空港。括弧の中は「どこにある空港か」の補足で、
// 表示上の短縮名（shortAirportName）では落ちる。
const HND: DepartureAirport = { iata: 'HND', nameJa: '羽田空港（東京）' };
const NRT: DepartureAirport = { iata: 'NRT', nameJa: '成田空港（千葉）' };
const ITM: DepartureAirport = { iata: 'ITM', nameJa: '伊丹空港（大阪）' };
const KIX: DepartureAirport = { iata: 'KIX', nameJa: '関西国際空港（大阪）' };
const NGO: DepartureAirport = { iata: 'NGO', nameJa: '中部国際空港（セントレア）' };
const KMQ: DepartureAirport = { iata: 'KMQ', nameJa: '小松空港（石川）' };

export const DEPARTURE_PREFECTURES: DeparturePrefecture[] = [
  // ---- 北海道 -------------------------------------------------------------
  {
    code: 'hokkaido',
    nameJa: '北海道',
    region: '北海道',
    airports: [
      { iata: 'CTS', nameJa: '新千歳空港' },
      { iata: 'HKD', nameJa: '函館空港' },
      { iata: 'AKJ', nameJa: '旭川空港' },
      { iata: 'OBO', nameJa: 'とかち帯広空港' },
      { iata: 'KUH', nameJa: '釧路空港' },
      { iata: 'MMB', nameJa: '女満別空港' },
    ],
  },

  // ---- 東北 ---------------------------------------------------------------
  { code: 'aomori', nameJa: '青森県', region: '東北', airports: [{ iata: 'AOJ', nameJa: '青森空港' }, { iata: 'MSJ', nameJa: '三沢空港' }] },
  { code: 'iwate', nameJa: '岩手県', region: '東北', airports: [{ iata: 'HNA', nameJa: 'いわて花巻空港' }] },
  { code: 'miyagi', nameJa: '宮城県', region: '東北', airports: [{ iata: 'SDJ', nameJa: '仙台空港' }] },
  { code: 'akita', nameJa: '秋田県', region: '東北', airports: [{ iata: 'AXT', nameJa: '秋田空港' }, { iata: 'ONJ', nameJa: '大館能代空港' }] },
  { code: 'yamagata', nameJa: '山形県', region: '東北', airports: [{ iata: 'GAJ', nameJa: '山形空港' }, { iata: 'SYO', nameJa: '庄内空港' }] },
  { code: 'fukushima', nameJa: '福島県', region: '東北', airports: [{ iata: 'FKS', nameJa: '福島空港' }] },

  // ---- 関東 ---------------------------------------------------------------
  { code: 'ibaraki', nameJa: '茨城県', region: '関東', airports: [{ iata: 'IBR', nameJa: '茨城空港' }, NRT] },
  { code: 'tochigi', nameJa: '栃木県', region: '関東', borrowed: true, airports: [HND, NRT] },
  { code: 'gunma', nameJa: '群馬県', region: '関東', borrowed: true, airports: [HND, NRT] },
  { code: 'saitama', nameJa: '埼玉県', region: '関東', borrowed: true, airports: [HND, NRT] },
  { code: 'chiba', nameJa: '千葉県', region: '関東', airports: [{ iata: 'NRT', nameJa: '成田空港' }, HND] },
  { code: 'tokyo', nameJa: '東京都', region: '関東', airports: [HND, NRT] },
  { code: 'kanagawa', nameJa: '神奈川県', region: '関東', borrowed: true, airports: [HND, NRT] },

  // ---- 中部 ---------------------------------------------------------------
  { code: 'niigata', nameJa: '新潟県', region: '中部', airports: [{ iata: 'KIJ', nameJa: '新潟空港' }] },
  { code: 'toyama', nameJa: '富山県', region: '中部', airports: [{ iata: 'TOY', nameJa: '富山空港' }, KMQ] },
  { code: 'ishikawa', nameJa: '石川県', region: '中部', airports: [{ iata: 'KMQ', nameJa: '小松空港' }, { iata: 'NTQ', nameJa: '能登空港' }] },
  { code: 'fukui', nameJa: '福井県', region: '中部', borrowed: true, airports: [KMQ, ITM] },
  { code: 'yamanashi', nameJa: '山梨県', region: '中部', borrowed: true, airports: [HND] },
  { code: 'nagano', nameJa: '長野県', region: '中部', airports: [{ iata: 'MMJ', nameJa: '松本空港' }, HND] },
  { code: 'gifu', nameJa: '岐阜県', region: '中部', borrowed: true, airports: [NGO] },
  { code: 'shizuoka', nameJa: '静岡県', region: '中部', airports: [{ iata: 'FSZ', nameJa: '静岡空港' }] },
  { code: 'aichi', nameJa: '愛知県', region: '中部', airports: [{ iata: 'NGO', nameJa: '中部国際空港（セントレア）' }, { iata: 'NKM', nameJa: '県営名古屋空港（小牧）' }] },

  // ---- 近畿 ---------------------------------------------------------------
  { code: 'mie', nameJa: '三重県', region: '近畿', borrowed: true, airports: [NGO, KIX] },
  { code: 'shiga', nameJa: '滋賀県', region: '近畿', borrowed: true, airports: [ITM, KIX] },
  { code: 'kyoto', nameJa: '京都府', region: '近畿', borrowed: true, airports: [ITM, KIX] },
  { code: 'osaka', nameJa: '大阪府', region: '近畿', airports: [{ iata: 'ITM', nameJa: '伊丹空港' }, { iata: 'KIX', nameJa: '関西国際空港' }] },
  { code: 'hyogo', nameJa: '兵庫県', region: '近畿', airports: [{ iata: 'UKB', nameJa: '神戸空港' }, ITM, { iata: 'TJH', nameJa: '但馬空港' }] },
  { code: 'nara', nameJa: '奈良県', region: '近畿', borrowed: true, airports: [ITM, KIX] },
  { code: 'wakayama', nameJa: '和歌山県', region: '近畿', airports: [{ iata: 'SHM', nameJa: '南紀白浜空港' }, KIX] },

  // ---- 中国 ---------------------------------------------------------------
  { code: 'tottori', nameJa: '鳥取県', region: '中国', airports: [{ iata: 'TTJ', nameJa: '鳥取空港' }, { iata: 'YGJ', nameJa: '米子空港' }] },
  { code: 'shimane', nameJa: '島根県', region: '中国', airports: [{ iata: 'IZO', nameJa: '出雲空港' }, { iata: 'IWJ', nameJa: '萩・石見空港' }] },
  { code: 'okayama', nameJa: '岡山県', region: '中国', airports: [{ iata: 'OKJ', nameJa: '岡山空港' }] },
  { code: 'hiroshima', nameJa: '広島県', region: '中国', airports: [{ iata: 'HIJ', nameJa: '広島空港' }] },
  { code: 'yamaguchi', nameJa: '山口県', region: '中国', airports: [{ iata: 'UBJ', nameJa: '山口宇部空港' }, { iata: 'IWK', nameJa: '岩国錦帯橋空港' }] },

  // ---- 四国 ---------------------------------------------------------------
  { code: 'tokushima', nameJa: '徳島県', region: '四国', airports: [{ iata: 'TKS', nameJa: '徳島空港' }] },
  { code: 'kagawa', nameJa: '香川県', region: '四国', airports: [{ iata: 'TAK', nameJa: '高松空港' }] },
  { code: 'ehime', nameJa: '愛媛県', region: '四国', airports: [{ iata: 'MYJ', nameJa: '松山空港' }] },
  { code: 'kochi', nameJa: '高知県', region: '四国', airports: [{ iata: 'KCZ', nameJa: '高知空港' }] },

  // ---- 九州 ---------------------------------------------------------------
  { code: 'fukuoka', nameJa: '福岡県', region: '九州', airports: [{ iata: 'FUK', nameJa: '福岡空港' }, { iata: 'KKJ', nameJa: '北九州空港' }] },
  { code: 'saga', nameJa: '佐賀県', region: '九州', airports: [{ iata: 'HSG', nameJa: '佐賀空港' }, { iata: 'FUK', nameJa: '福岡空港' }] },
  { code: 'nagasaki', nameJa: '長崎県', region: '九州', airports: [{ iata: 'NGS', nameJa: '長崎空港' }] },
  { code: 'kumamoto', nameJa: '熊本県', region: '九州', airports: [{ iata: 'KMJ', nameJa: '熊本空港' }] },
  { code: 'oita', nameJa: '大分県', region: '九州', airports: [{ iata: 'OIT', nameJa: '大分空港' }] },
  { code: 'miyazaki', nameJa: '宮崎県', region: '九州', airports: [{ iata: 'KMI', nameJa: '宮崎空港' }] },
  { code: 'kagoshima', nameJa: '鹿児島県', region: '九州', airports: [{ iata: 'KOJ', nameJa: '鹿児島空港' }] },

  // ---- 沖縄 ---------------------------------------------------------------
  { code: 'okinawa', nameJa: '沖縄県', region: '沖縄', airports: [{ iata: 'OKA', nameJa: '那覇空港' }, { iata: 'ISG', nameJa: '石垣空港' }, { iata: 'MMY', nameJa: '宮古空港' }] },
];

/** 既定の出発地（最も利用者が多い想定） */
export const DEFAULT_PREF_CODE = 'tokyo';

export function findPrefByCode(code: string | null | undefined): DeparturePrefecture | undefined {
  if (!code) return undefined;
  return DEPARTURE_PREFECTURES.find((p) => p.code === code);
}

/**
 * IATA から都道府県を逆引き（旧バージョンで保存された IATA のみの設定を引き継ぐため）。
 * 同じ空港を複数県が共有する（HND など）ので、**最初に一致した県**を返す。
 */
export function findPrefByIata(iata: string | null | undefined): DeparturePrefecture | undefined {
  if (!iata) return undefined;
  const up = iata.toUpperCase();
  return DEPARTURE_PREFECTURES.find((p) => p.airports.some((a) => a.iata === up));
}

/** 県 + IATA から空港を引く。県に無い IATA なら先頭（既定）にフォールバック */
export function resolveAirport(pref: DeparturePrefecture, iata: string | null | undefined): DepartureAirport {
  const up = (iata || '').toUpperCase();
  return pref.airports.find((a) => a.iata === up) ?? pref.airports[0]!;
}

/** 「東京都・羽田空港（HND）」のような表示用ラベル */
export function departureLabel(pref: DeparturePrefecture, air: DepartureAirport): string {
  return `${pref.nameJa}・${shortAirportName(air)}（${air.iata}）`;
}

/** 「羽田空港」— 括弧の補足を落とした短い空港名 */
export function shortAirportName(air: DepartureAirport): string {
  return air.nameJa.replace(/（.*/, '');
}
